import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';

import { getStudentEmailDomain } from '../../api/environment';
import { featureEnabled } from '../../config/features';
import { useQltIztContentStore } from '../../stores/qlt';
import { useExamListLoad, type ClassScope } from '../exams/useExamResults';
import { useSubmissionPrefetch } from '../submissions/useClassSubmissions';
import '../../styles/submissions.css';

export type LibrarySection = 'lectures' | 'homework' | 'exams';

const LABELS: Record<LibrarySection, string> = {
  lectures: 'Học liệu',
  homework: 'Bài tập',
  exams: 'Bài kiểm tra',
};

/** Kho bài's sections; bài tập and bài kiểm tra carry the class's submissions, so each needs its data source. */
export function librarySections(): LibrarySection[] {
  const out: LibrarySection[] = ['lectures'];
  if (!featureEnabled('board.submissions')) return out;
  out.push('homework');
  if (featureEnabled('board.examResults') && getStudentEmailDomain()) out.push('exams');
  return out;
}

// after the Học liệu tree, so the warm-up never competes with the first rows on screen
const PREFETCH_DELAY_MS = 1500;

/** Reads Bài tập and the Bài kiểm tra list in the background once Kho bài is open and idle. */
function useClassWorkPrefetch(scope: ClassScope, sections: LibrarySection[]) {
  const treeLoading = useQltIztContentStore((state) => state.loading);
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (treeLoading) return undefined;
    const timer = window.setTimeout(() => setIdle(true), PREFETCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [treeLoading]);
  useSubmissionPrefetch(idle && sections.indexOf('homework') >= 0);
  useExamListLoad(scope, idle && sections.indexOf('exams') >= 0);
}

export interface LibrarySectionsProps {
  value: LibrarySection;
  onChange: (section: LibrarySection) => void;
  /** The class the tabs read for. */
  scope: ClassScope;
  children: ReactNode;
}

// Học liệu | Bài tập | Bài kiểm tra: Bài nộp lives here now, beside the material it belongs to
export function LibrarySections({ value, onChange, scope, children }: LibrarySectionsProps) {
  const sections = librarySections();
  useClassWorkPrefetch(scope, sections);
  if (sections.length < 2) return <>{children}</>;

  function onKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const at = sections.indexOf(value) + (event.key === 'ArrowRight' ? 1 : -1);
    onChange(sections[(at + sections.length) % sections.length]);
  }

  return (
    <>
      <div className="up-tabs hw-tabs" role="tablist" aria-label="Mục trong kho bài">
        {sections.map((id) => (
          <button key={id} type="button" role="tab" id={`library-tab-${id}`} aria-selected={value === id} aria-controls="library-tabpanel" tabIndex={value === id ? 0 : -1} className={value === id ? 'is-active' : ''} onKeyDown={onKey} onClick={() => onChange(id)}>{LABELS[id]}</button>
        ))}
      </div>
      <div role="tabpanel" id="library-tabpanel" aria-labelledby={`library-tab-${value}`}>{children}</div>
    </>
  );
}
